import * as $ from 'jquery';

import { Observable } from 'common/barrels/rx';
import { disposableSubscription, emptyDisposable } from 'common/barrels/core';
import { from, wheelEvent, wheelResolution, mousedownleft, mousemove, mouseupleft } from 'common/rxui/core';
import 'common/styles/rxui/scroll.less';

type ScrollDirection = 'vertical' | 'horizontal' | 'both';

type WheelDimension = 'vertical' | 'horizontal';

class ScrollInfo {
    constructor(public delta: number, public dimension: WheelDimension) { }
}

function scrollDrag($element: JQuery, dimension: WheelDimension, scale = 1): Observable<ScrollInfo> {
    const down = mousedownleft($element).do(() => $('body').addClass('noselect'));
    const up = mouseupleft($(document)).do(() => $('body').removeClass('noselect'));
    return down
        .switchMap(() => mousemove($(document)).pairwise().map(([a, b]) => dimension === 'vertical' ? b.screenY - a.screenY : b.screenX - a.screenX).takeUntil(up))
        .map(x => new ScrollInfo(x * scale, dimension));
}

export function unobtrusiveScroll($element: JQuery, direction: ScrollDirection, recalcTrigger: Observable<any>, $indicatorContainer?: JQuery, indicatorOffset?: number): JQuery {
    $element.css('overflow', 'hidden');

    recalcTrigger = recalcTrigger.share();

    const h = direction !== 'vertical';
    const v = direction !== 'horizontal';

    const scrollbar = (x: string) => $('<scrollbar>').addClass(x);

    const $h = h ? scrollbar('rxui-scrollindicator rxui-scrollindicator-h').appendTo($indicatorContainer || $element) : $();
    const $v = v ? scrollbar('rxui-scrollindicator rxui-scrollindicator-v').appendTo($indicatorContainer || $element) : $();

    const dragScale = recalcTrigger.map(() => ({
        h: $h ? $element.width() / $h.width() : 1,
        v: $v ? $element.height() / $v.height() : 1
    }));

    const drags = dragScale.switchMap(s => Observable.merge(
        h ? scrollDrag($h, 'horizontal', s.h) : Observable.never<ScrollInfo>(),
        v ? scrollDrag($v, 'vertical', s.v) : Observable.never<ScrollInfo>()
    ));

    const enter = Observable.fromEvent($element, 'mouseenter');
    const leave = Observable.fromEvent($element, 'mouseleave');
    const hovering = enter.map(() => true).merge(leave.map(() => false)).startWith(false);

    const opacityProgression = () => Observable.interval(10).take(100).takeUntil(enter).map(x => 1 - ((x + 1) / 100));
    const leaving = leave.switchMap(opacityProgression).share();

    // const hRecalcTrigger = recalcTrigger.map(_ => $element.width()).distinctUntilChanged().share();
    // const vRecalcTrigger = recalcTrigger.map(_ => $element.height()).distinctUntilChanged().share();

    // the above logic was efficient with respect to the container changing size, but did not accommodate changes made inside the scrolled content (expanding / collapsing, for example).
    // It has bee silenced for now. If there are performance issues as a result, this logic will have to be broken into two streams: one for the container changing size, and another
    // for the content changing size.
    const hRecalcTrigger = recalcTrigger.map(_ => $element.width()).share();
    const vRecalcTrigger = recalcTrigger.map(_ => $element.height()).share();

    const adjustedWhileNotHovering = trigger => trigger.whereLatest(hovering.map(x => !x)).switchMap(opacityProgression);

    const popIn = trigger => enter.merge(trigger);

    $element.owns(disposableSubscription(
        h ? hRecalcTrigger.subscribe(() => update($element, $h, 'width', 'left', x => x[0].scrollLeft, x => x[0].scrollWidth, x => x.width())) : emptyDisposable,
        v ? vRecalcTrigger.subscribe(() => update($element, $v, 'height', 'top', x => x[0].scrollTop, x => x[0].scrollHeight, x => x.height())) : emptyDisposable,

        reinventedWheel($element, direction).merge(drags).subscribe(x => {
            if (x.dimension === 'horizontal') {
                $element.scrollLeft($element.scrollLeft() + x.delta);
            } else {
                $element.scrollTop($element.scrollTop() + x.delta);
            }
        }),

        from($element, 'scroll').subscribe(() => {
            if (v) {
                whileTemporarilyHidden(
                    $v,
                    () => updateIndicatorPosition($element, $v, 'top', x => x[0].scrollTop, x => x[0].scrollHeight, x => x.height(), $indicatorContainer || $element, indicatorOffset || 0)
                );

                if (h)
                    $h.css('bottom', 0 - $element[0].scrollTop);
            }

            if (h) {
                whileTemporarilyHidden(
                    $h,
                    () => updateIndicatorPosition($element, $h, 'left', x => x[0].scrollLeft, x => x[0].scrollWidth, x => x.width(), $indicatorContainer || $element, indicatorOffset || 0)
                );

                if (v)
                    $v.css('right', 0 - $element[0].scrollLeft);
            }
        }),

        h ? adjustedWhileNotHovering(hRecalcTrigger).subscribe(x => $h.css('opacity', x)) : emptyDisposable,
        v ? adjustedWhileNotHovering(vRecalcTrigger).subscribe(x => $v.css('opacity', x)) : emptyDisposable,

        h ? popIn(hRecalcTrigger).subscribe(_ => $h.css('opacity', 1)) : emptyDisposable,
        v ? popIn(vRecalcTrigger).subscribe(_ => $v.css('opacity', 1)) : emptyDisposable,

        h ? leaving.subscribe(x => $h.css('opacity', x)) : emptyDisposable,
        v ? leaving.subscribe(x => $v.css('opacity', x)) : emptyDisposable
    ));

    return $element;
}

function inBottomNPercent($element: JQuery, targetPercentile: number, offset: number): boolean {
    const percentile = (offset - $element[0].scrollTop) / $element.height() * 100;
    return (100 - percentile) <= targetPercentile;
}

function reinventedWheel($element: JQuery, applicable: ScrollDirection) {
    return wheelEvent($element).map(x => new ScrollInfo(
        x.deltaY / Math.abs(x.deltaY) * wheelResolution,
              applicable === 'vertical' ? 'vertical'
            : applicable === 'horizontal' || inBottomNPercent($element, 20, x.offsetY) ? 'horizontal'
            : 'vertical'
    ));
}

function update($element: JQuery, $i: JQuery, cssSize: string, cssPosition: string,
    scrollPositionAccessor: (x: JQuery) => number,
    scrollDimensionAccessor: (x: JQuery) => number,
    dimensionAccessor: (x: JQuery) => number
) {
    const required = scrollDimensionAccessor($element);
    const available = dimensionAccessor($element);
    const indicatorSize = available * available / required;

    if (Math.abs(required - available) > 1)
        $i.show();
    else
        $i.hide();

    $i.css(cssSize, indicatorSize);

    const scrollPosition = scrollPositionAccessor($element);
    const scrollPerc = (scrollPosition + available) / required;
    $i.css(cssPosition, scrollPosition + scrollPerc * available - dimensionAccessor($i));
}

function updateIndicatorPosition($element: JQuery, $i: JQuery, cssPosition: string,
    scrollPositionAccessor: (x: JQuery) => number,
    scrollDimensionAccessor: (x: JQuery) => number,
    dimensionAccessor: (x: JQuery) => number,
    $indicatorContainer: JQuery,
    indicatorOffset: number
) {
    const required = scrollDimensionAccessor($element);
    const available = dimensionAccessor($element);
    const indicatorDimension = dimensionAccessor($i);

    const scrollPosition = scrollPositionAccessor($element);
    const scrollPerc = (scrollPosition + available) / required;

    const scrollPositionOffset = $element[0] === $indicatorContainer[0] ? scrollPosition : 0;

    const value = scrollPositionOffset + scrollPerc * available - indicatorDimension + indicatorOffset;
    $i.css(cssPosition, value);
}

function whileTemporarilyHidden($element: JQuery, f: () => void) {
    const wasVisibleBefore = $element.is(':visible');
    if (wasVisibleBefore)
        $element.hide();

    f();

    if (wasVisibleBefore)
        $element.show();
}